import React from "react";
import { useTranslation } from "utilities/i18n";
import { SortState } from "store/filters";
import { hasValue } from "utilities/filters/filters";
import { PolyIconButton, FloatingDropdownList as DropdownList } from "elements";

export type FilterOption = {
  text: string;
  value: string;
  count?: number;
};

export interface FilterProps<T> {
  title: string;
  field: keyof T;
  filterValues: T;
  options: Array<FilterOption>;
  enableTooltip?: boolean;
  onChange: (value: any) => void;
  sort?: boolean;
  search?: boolean;
  loading?: boolean;
  clearable?: boolean;
  additionalClasses?: string;
  customValue?: boolean;
}

function Filter<T extends {}>(props: FilterProps<T>): JSX.Element {
  const {
    title,
    field,
    options,
    additionalClasses,
    enableTooltip = false,
    onChange,
    filterValues,
    sort = false,
    search = false,
    loading = false,
    clearable = false,
    customValue = false
  } = props;

  const { t } = useTranslation();

  const handleChange = (selection: string, oldValue: SortState) => {
    if (sort) {
      const dir = oldValue ? oldValue.direction : "DESC";
      if (selection !== "") {
        onChange({ field: selection, direction: dir });
      } else {
        onChange(undefined);
      }
    } else {
      onChange(selection);
    }
  };

  const toggleSortOrder = (oldSort: SortState) => {
    const newSort = {
      field: oldSort.field,
      direction: oldSort.direction === "ASC" ? "DESC" : "ASC"
    };
    onChange(newSort);
  };

  const hasSelected = hasValue(filterValues, field);

  const filterValue: any = filterValues[field];
  let value = filterValue;
  if (sort && value) {
    value = value.field;
  }

  const ascending = filterValue?.direction === "ASC";

  const classes = `floating-label-filter ${
    hasSelected ? "selected" : ""
  } ${additionalClasses ?? ""}`.trim();

  return (
    <div className={classes}>
      {sort && hasSelected ? (
        <PolyIconButton
          icon="arrow_up"
          title={ascending ? t("ASCENDING") : t("DESCENDING")}
          className="filter-arrow"
          iconClassName={ascending ? "desc" : ""}
          onClick={() => toggleSortOrder(filterValue)}
        />
      ) : null}
      <DropdownList
        name={title}
        options={options}
        enableTooltip={enableTooltip}
        onChange={val => handleChange(val, filterValue)}
        label={title}
        value={hasSelected ? value : ""}
        search={search}
        busy={loading}
        clearable={clearable}
        customValue={customValue}
      />
    </div>
  );
}

export default Filter;
